import type * as React from 'react';
import { Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconTile, type IconTileTone } from './icon-tile';
import { ProgressBar, type ProgressTone } from './progress-bar';

/** Bordered list of work-item rows with hairlines between them. */
function WorkItemList({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="work-item-list"
      className={cn('divide-y overflow-hidden rounded-lg border', className)}
      {...props}
    />
  );
}

interface WorkItemProgress {
  value: number;
  max?: number;
  /** Accessible name of the bar. */
  label: string;
  /** Visible text next to the bar, also used as `aria-valuetext` when it is a string. */
  text?: React.ReactNode;
  tone?: ProgressTone;
}

/**
 * Work-item row (DESIGN_SYSTEM §4, T-093): one thing that needs a person. A 40 px icon tile
 * tinted by kind, then meta (12 muted) → title (15/22 600) → detail (13 muted) → optional
 * progress; the right side holds the SLA badge over the actions. `done` swaps the tile for a
 * success tick and mutes the title (the caller passes the success badge and Undo).
 * Render inside `WorkItemList`.
 */
function WorkItemRow({
  icon,
  tone = 'primary',
  meta,
  title,
  titleAs: Title = 'p',
  detail,
  progress,
  badge,
  actions,
  done = false,
  className,
  ...props
}: Omit<React.ComponentProps<'li'>, 'title' | 'children'> & {
  icon: LucideIcon;
  tone?: IconTileTone;
  meta?: React.ReactNode;
  title: React.ReactNode;
  /** A heading when the rows are the main content of a page section. */
  titleAs?: 'p' | 'h3' | 'h4';
  detail?: React.ReactNode;
  progress?: WorkItemProgress;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  done?: boolean;
}) {
  const hasRight = Boolean(badge) || Boolean(actions);
  return (
    <li
      data-slot="work-item-row"
      data-done={done || undefined}
      className={cn('flex flex-wrap items-start gap-3.5 px-4 py-4 sm:px-6', className)}
      {...props}
    >
      <IconTile icon={done ? Check : icon} tone={done ? 'success' : tone} />
      <div className="flex min-w-0 flex-1 basis-48 flex-col gap-0.5">
        {meta ? <p className="text-caption text-muted-foreground">{meta}</p> : null}
        <Title className={cn('text-item', done ? 'text-muted-foreground' : 'text-foreground')}>
          {title}
        </Title>
        {detail ? <p className="text-small text-muted-foreground">{detail}</p> : null}
        {progress ? (
          <div className="mt-1.5 flex items-center gap-2.5">
            <ProgressBar
              value={progress.value}
              max={progress.max}
              label={progress.label}
              valueText={typeof progress.text === 'string' ? progress.text : undefined}
              tone={progress.tone}
              className="max-w-50 min-w-16 flex-1"
            />
            {progress.text ? (
              <span className="shrink-0 text-caption text-muted-foreground">{progress.text}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {hasRight ? (
        <div className="ml-auto flex flex-none flex-col items-end gap-2">
          {badge}
          {actions ? <div className="flex flex-wrap justify-end gap-1.5">{actions}</div> : null}
        </div>
      ) : null}
    </li>
  );
}

export { WorkItemList, WorkItemRow, type WorkItemProgress };
